import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { DealType, PhaseAutomation } from "@/lib/types"

export function useDealTypes() {
  return useQuery({
    queryKey: ["deal_types"],
    queryFn: () => api.get<{ data: DealType[] }>("/api/v1/deal_types"),
  })
}

export function useDealType(id?: string) {
  return useQuery({
    queryKey: ["deal_types", id],
    queryFn: () => api.get<{ data: DealType }>(`/api/v1/deal_types/${id}`),
    enabled: !!id,
  })
}

export function useCreateDealType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: DealType }>("/api/v1/deal_types", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal_types"] }),
  })
}

export function useUpdateDealType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: DealType }>(`/api/v1/deal_types/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal_types"] }),
  })
}

export function useDeleteDealType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/deal_types/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal_types"] }),
  })
}

export function useCreatePhaseAutomation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ dealPhaseId, data }: { dealPhaseId: string; data: Record<string, unknown> }) =>
      api.post<{ data: PhaseAutomation }>(`/api/v1/deal_phases/${dealPhaseId}/phase_automations`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal_types"] }),
  })
}

export function useUpdatePhaseAutomation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ dealPhaseId, id, data }: { dealPhaseId: string; id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: PhaseAutomation }>(`/api/v1/deal_phases/${dealPhaseId}/phase_automations/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal_types"] }),
  })
}

export function useDeletePhaseAutomation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ dealPhaseId, id }: { dealPhaseId: string; id: string }) =>
      api.delete(`/api/v1/deal_phases/${dealPhaseId}/phase_automations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal_types"] }),
  })
}
